import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Sitemap } from '../components/Sitemap';
import { VideoCard } from '../components/VideoCard';
import { VideoNavigation } from '../components/VideoNavigation';
import { SearchIcon, FilterIcon, XIcon, PlayCircleIcon, BookmarkIcon, ClockIcon, TagIcon, GridIcon, LayoutListIcon, TrendingUpIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const VIDEO_CATEGORIES = [{
  id: 'beginner',
  title: {
    en: 'Beginner Lessons',
    ar: 'دروس للمبتدئين'
  },
  videos: [{
    id: 'v1',
    title: 'English Alphabet Pronunciation',
    titleAr: 'نطق الحروف الأبجدية الإنجليزية',
    thumbnail: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '8:24',
    category: 'Pronunciation',
    categoryAr: 'النطق',
    featured: true,
    tags: ['beginner', 'pronunciation']
  }, {
    id: 'v2',
    title: 'Common Greetings in English',
    titleAr: 'التحيات الشائعة باللغة الإنجليزية',
    thumbnail: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '6:15',
    category: 'Conversation',
    categoryAr: 'المحادثة',
    featured: false,
    tags: ['beginner', 'conversation']
  }, {
    id: 'v3',
    title: 'Numbers 1-100 in English',
    titleAr: 'الأرقام من 1-100 باللغة الإنجليزية',
    thumbnail: 'https://images.unsplash.com/photo-1457369804613-52c61a468e7d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '12:40',
    category: 'Vocabulary',
    categoryAr: 'المفردات',
    featured: false,
    tags: ['beginner', 'vocabulary']
  }]
}, {
  id: 'intermediate',
  title: {
    en: 'Intermediate Lessons',
    ar: 'دروس متوسطة المستوى'
  },
  videos: [{
    id: 'v4',
    title: 'Present Perfect Tense Explained',
    titleAr: 'شرح زمن المضارع التام',
    thumbnail: 'https://images.unsplash.com/photo-1588702547923-7093a6c3ba33?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '14:30',
    category: 'Grammar',
    categoryAr: 'القواعد',
    featured: true,
    tags: ['intermediate', 'grammar']
  }, {
    id: 'v5',
    title: 'How to Order Food in English',
    titleAr: 'كيفية طلب الطعام باللغة الإنجليزية',
    thumbnail: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '9:45',
    category: 'Conversation',
    categoryAr: 'المحادثة',
    featured: false,
    tags: ['intermediate', 'conversation', 'practical']
  }]
}, {
  id: 'advanced',
  title: {
    en: 'Advanced Lessons',
    ar: 'دروس متقدمة'
  },
  videos: [{
    id: 'v6',
    title: 'English Idioms and Expressions',
    titleAr: 'التعبيرات والاصطلاحات الإنجليزية',
    thumbnail: 'https://images.unsplash.com/photo-1580894732444-8ecded7900cd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '18:20',
    category: 'Vocabulary',
    categoryAr: 'المفردات',
    featured: true,
    tags: ['advanced', 'vocabulary', 'idioms']
  }, {
    id: 'v7',
    title: 'Business English for Meetings',
    titleAr: 'الإنجليزية للأعمال للاجتماعات',
    thumbnail: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    duration: '22:15',
    category: 'Business',
    categoryAr: 'الأعمال',
    featured: false,
    tags: ['advanced', 'business']
  }]
}];
const ALL_VIDEOS = VIDEO_CATEGORIES.flatMap(category => category.videos.map(video => ({
  ...video,
  levelId: category.id,
  level: {
    en: category.title.en,
    ar: category.title.ar
  }
})));
export function VideoLessons() {
  const {
    isArabic
  } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const filteredVideos = ALL_VIDEOS.filter(video => {
    const matchesSearch = searchQuery === '' || video.title.toLowerCase().includes(searchQuery.toLowerCase()) || video.titleAr.includes(searchQuery);
    const matchesFilters = activeFilters.length === 0 || activeFilters.some(filter => video.tags.includes(filter) || video.levelId === filter);
    return matchesSearch && matchesFilters;
  });
  const toggleFilter = (filter: string) => {
    if (activeFilters.includes(filter)) {
      setActiveFilters(activeFilters.filter(f => f !== filter));
    } else {
      setActiveFilters([...activeFilters, filter]);
    }
  };
  const clearFilters = () => {
    setActiveFilters([]);
    setSearchQuery('');
  };
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-4589">
      <Sitemap currentPage={{
      en: 'Video Lessons',
      ar: 'دروس الفيديو'
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }]} data-id="element-4590" />
      <div className="max-w-7xl mx-auto" data-id="element-4591">
        <div className="text-center mb-12" data-id="element-4592">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-4593">
            {isArabic ? 'دروس الفيديو' : 'Video Lessons'}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-4594">
            {isArabic ? 'تعلم اللغة الإنجليزية من خلال دروس فيديو تفاعلية مع شرح مفصل' : 'Learn English through interactive video lessons with detailed explanations'}
          </p>
        </div>
        <VideoNavigation activeTab="lessons" data-id="element-4595" />
        <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-4596">
          <div className="relative flex-1" data-id="element-4597">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" data-id="element-4598" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={isArabic ? 'ابحث عن دروس الفيديو...' : 'Search video lessons...'} className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400" data-id="element-4599" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" data-id="element-4600">
                <XIcon className="w-5 h-5" data-id="element-4601" />
              </button>}
          </div>
          <div className="flex gap-2" data-id="element-4602">
            <button onClick={() => setViewMode('grid')} className={`p-3 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'}`} data-id="element-4603">
              <GridIcon className="w-5 h-5" data-id="element-4604" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-3 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'}`} data-id="element-4605">
              <LayoutListIcon className="w-5 h-5" data-id="element-4606" />
            </button>
          </div>
        </div>
        <div className="mb-8" data-id="element-4607">
          <div className="flex items-center justify-between mb-3" data-id="element-4608">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300" data-id="element-4609">
              <FilterIcon className="w-5 h-5" data-id="element-4610" />
              <span className="font-medium" data-id="element-4611">
                {isArabic ? 'تصفية حسب:' : 'Filter by:'}
              </span>
            </div>
            {activeFilters.length > 0 && <button onClick={clearFilters} className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-4612">
                {isArabic ? 'مسح التصفية' : 'Clear filters'}
              </button>}
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-4613">
            {['beginner', 'intermediate', 'advanced'].map(level => <button key={level} onClick={() => toggleFilter(level)} className={`px-3 py-1.5 rounded-full text-sm font-medium ${activeFilters.includes(level) ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-4614">
                {level === 'beginner' ? isArabic ? 'مبتدئ' : 'Beginner' : level === 'intermediate' ? isArabic ? 'متوسط' : 'Intermediate' : isArabic ? 'متقدم' : 'Advanced'}
              </button>)}
            {['grammar', 'vocabulary', 'conversation', 'pronunciation', 'business', 'practical'].map(category => <button key={category} onClick={() => toggleFilter(category)} className={`px-3 py-1.5 rounded-full text-sm font-medium ${activeFilters.includes(category) ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-4615">
                {category === 'grammar' ? isArabic ? 'القواعد' : 'Grammar' : category === 'vocabulary' ? isArabic ? 'المفردات' : 'Vocabulary' : category === 'conversation' ? isArabic ? 'المحادثة' : 'Conversation' : category === 'pronunciation' ? isArabic ? 'النطق' : 'Pronunciation' : category === 'business' ? isArabic ? 'الأعمال' : 'Business' : isArabic ? 'عملي' : 'Practical'}
              </button>)}
          </div>
        </div>
        {activeFilters.length === 0 && searchQuery === '' && <div className="mb-12" data-id="element-4616">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-4617">
              {isArabic ? 'الفيديوهات المميزة' : 'Featured Videos'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4618">
              {ALL_VIDEOS.filter(video => video.featured).map(video => <Link key={video.id} to={`/learn/video-lessons/${video.id}`} data-id="element-4619">
                  <VideoCard title={video.title} titleAr={video.titleAr} thumbnail={video.thumbnail} duration={video.duration} category={isArabic ? video.categoryAr : video.category} data-id="element-4620" />
                </Link>)}
            </div>
          </div>}
        {filteredVideos.length > 0 ? <div data-id="element-4621">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-4622">
              {activeFilters.length > 0 || searchQuery !== '' ? isArabic ? 'نتائج البحث' : 'Search Results' : isArabic ? 'جميع الفيديوهات' : 'All Videos'}
            </h2>
            {viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-4623">
                {filteredVideos.map(video => <Link key={video.id} to={`/learn/video-lessons/${video.id}`} data-id="element-4624">
                    <VideoCard title={video.title} titleAr={video.titleAr} thumbnail={video.thumbnail} duration={video.duration} category={isArabic ? video.categoryAr : video.category} data-id="element-4625" />
                  </Link>)}
              </div> : <div className="space-y-4" data-id="element-4626">
                {filteredVideos.map(video => <Link key={video.id} to={`/learn/video-lessons/${video.id}`} className="flex bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-200" data-id="element-4627">
                    <div className="relative w-1/3" data-id="element-4628">
                      <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" data-id="element-4629" />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center" data-id="element-4630">
                        <PlayCircleIcon className="w-10 h-10 text-white opacity-80" data-id="element-4631" />
                      </div>
                      <div className="absolute bottom-2 right-2 flex items-center bg-black/70 rounded-full px-2 py-1" data-id="element-4632">
                        <ClockIcon className="w-3 h-3 text-white mr-1" data-id="element-4633" />
                        <span className="text-xs text-white" data-id="element-4634">
                          {video.duration}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 p-4" data-id="element-4635">
                      <div className="flex items-center justify-between mb-2" data-id="element-4636">
                        <div className="inline-block px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-full" data-id="element-4637">
                          {isArabic ? video.categoryAr : video.category}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4638">
                          {isArabic ? video.level.ar : video.level.en}
                        </div>
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1" data-id="element-4639">
                        {video.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400" dir="rtl" data-id="element-4640">
                        {video.titleAr}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2" data-id="element-4641">
                        {video.tags.map(tag => <div key={tag} className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded" data-id="element-4642">
                            #{tag}
                          </div>)}
                      </div>
                    </div>
                  </Link>)}
              </div>}
          </div> : <div className="text-center py-12" data-id="element-4643">
            <div className="text-gray-400 dark:text-gray-500 mb-3" data-id="element-4644">
              <SearchIcon className="w-12 h-12 mx-auto" data-id="element-4645" />
            </div>
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2" data-id="element-4646">
              {isArabic ? 'لا توجد نتائج' : 'No results found'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-4647">
              {isArabic ? 'حاول البحث بكلمات مختلفة أو تعديل المرشحات' : 'Try searching with different terms or adjusting your filters'}
            </p>
            <button onClick={clearFilters} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-4648">
              {isArabic ? 'مسح التصفية' : 'Clear filters'}
            </button>
          </div>}
        <div className="mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 md:p-8" data-id="element-4649">
          <div className="flex flex-col md:flex-row items-center gap-6" data-id="element-4650">
            <div className="md:w-1/2" data-id="element-4651">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3" data-id="element-4652">
                {isArabic ? 'جرب فيديوهاتنا التعليمية القصيرة!' : 'Try our Short Educational Videos!'}
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-4653">
                {isArabic ? 'تعلم الإنجليزية بطريقة ممتعة من خلال فيديوهات قصيرة ومنشورات تعليمية مصممة خصيصًا لمساعدتك على التعلم بسرعة.' : 'Learn English in a fun way through short videos and educational posts specifically designed to help you learn quickly.'}
              </p>
              <Link to="/learn/educational-videos" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-4654">
                <TrendingUpIcon className="w-5 h-5" data-id="element-4655" />
                <span data-id="element-4656">
                  {isArabic ? 'استكشف الفيديوهات التعليمية' : 'Explore Educational Videos'}
                </span>
              </Link>
            </div>
            <div className="md:w-1/2 flex justify-center" data-id="element-4657">
              <div className="relative w-64 h-64" data-id="element-4658">
                <div className="absolute top-0 left-0 w-56 h-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg transform -rotate-6" data-id="element-4659">
                  <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Educational content" className="w-full h-full object-cover rounded-lg" data-id="element-4660" />
                </div>
                <div className="absolute bottom-0 right-0 w-56 h-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg transform rotate-6" data-id="element-4661">
                  <img src="https://images.unsplash.com/photo-1543165796-5426273eaab3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Educational content" className="w-full h-full object-cover rounded-lg" data-id="element-4662" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}